/* global Excel */
import { distributeSchedule, monthLabel } from "./scheduleMath";
// Working tools over the detail (child) sheets of a BOQ workbook:
//   - auditBoq:            finds missing prices/units/quantities and inconsistent prices
//   - buildProcurementSheet: purchase / labour / equipment lists summed over all sheets
//   - scaleQuantities:     multiplies quantities (same job, different size)
//
// Child sheet layout (custom-boq-table.js): A=N, B=description, C=unit, E=quantity,
// F/H/J = material/labour/equipment unit price, G/I/K = their totals, L=row total.
// Data starts at row 9; the first "ჯამი"/Total row in column B ends the data.

const SUM_WORDS = new Set(["ჯამი", "total", "итого"]);
const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const norm = (s) => String(s ?? "").trim().replace(/\s+/g, " ").toLowerCase();
const colLetter = (i) => String.fromCharCode(65 + i);

/** True for detail sheets: row 4 holds the column numbers 1,2,3,4. */
function isChildSheet(values) {
  const r = values[3] || [];
  return String(r[0]).trim() === "1" && String(r[1]).trim() === "2" && String(r[2]).trim() === "3" && String(r[3]).trim() === "4";
}

/** Loads every detail sheet with its parsed data rows. */
async function readChildSheets(context) {
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();

  const ranges = sheets.items.map((s) => {
    const r = s.getUsedRangeOrNullObject();
    r.load(["values", "formulas", "rowIndex"]);
    return { name: s.name, range: r };
  });
  await context.sync();

  const out = [];
  ranges.forEach(({ name, range }) => {
    if (range.isNullObject || range.rowIndex !== 0) return; // layout starts at A1
    const values = range.values;
    if (!values || values.length < 10 || !isChildSheet(values)) return;

    const rows = [];
    for (let i = 8; i < values.length; i += 1) {
      const v = values[i];
      if (SUM_WORDS.has(norm(v[1]))) break;
      const qty = toNum(v[4]);
      const matPrice = toNum(v[5]);
      const labPrice = toNum(v[7]);
      const eqPrice = toNum(v[9]);
      const kind = labPrice > 0 ? "LABOUR" : eqPrice > 0 ? "EQUIPMENT" : matPrice > 0 ? "MATERIAL" : "";
      rows.push({
        sheet: name,
        row: i + 1,
        id: String(v[0] ?? "").trim(),
        title: String(v[1] ?? "").trim(),
        unit: String(v[2] ?? "").trim(),
        qty,
        price: kind === "LABOUR" ? labPrice : kind === "EQUIPMENT" ? eqPrice : matPrice,
        kind,
        hasQty: String(v[4] ?? "").trim() !== "",
        hasAnyPrice: matPrice > 0 || labPrice > 0 || eqPrice > 0,
        negative: [v[4], v[5], v[7], v[9]].some((x) => toNum(x) < 0),
        qtyIsFormula: String(range.formulas[i][4] ?? "").startsWith("="),
      });
    }
    out.push({ name, rows });
  });
  return out;
}

export async function goToCell(sheetName, address) {
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    sheet.activate();
    sheet.getRange(address).select();
    await context.sync();
  });
}

// ───────────────────────────── Audit ─────────────────────────────

export async function auditBoq() {
  return Excel.run(async (context) => {
    const sheets = await readChildSheets(context);
    const issues = [];
    const add = (sev, r, msg, col = "B") =>
      issues.push({ sev, sheet: r.sheet, cell: `${col}${r.row}`, msg: `${r.id ? r.id + " " : ""}${r.title || "(უსახელო)"} — ${msg}` });

    const priceGroups = new Map(); // kind|title|unit -> [{price, sheet, row}]
    let rowCount = 0;

    sheets.forEach(({ name, rows }) => {
      const seen = new Map();
      let sheetTotal = 0;
      rows.forEach((r) => {
        const isLine = r.hasQty || r.hasAnyPrice;
        if (!isLine && !r.title) return;
        rowCount += isLine ? 1 : 0;
        if (!isLine) return; // plain heading row

        sheetTotal += r.qty * r.price;
        if (r.negative) add("error", r, "უარყოფითი მნიშვნელობა", "E");
        if (!r.title) add("error", r, "ფასი/რაოდენობა არის, სახელი არ არის");
        if (r.hasQty && r.qty > 0 && !r.hasAnyPrice) add("error", r, "ფასი არ არის მითითებული", "F");
        if (r.hasAnyPrice && (!r.hasQty || r.qty === 0)) add("error", r, "რაოდენობა არ არის მითითებული", "E");
        if (!r.unit) add("warn", r, "ერთეული არ არის მითითებული", "C");

        const dupKey = `${norm(r.title)}|${norm(r.unit)}`;
        if (r.title) {
          if (seen.has(dupKey)) add("warn", r, `დუბლირებულია (იხ. სტრიქონი ${seen.get(dupKey)})`);
          else seen.set(dupKey, r.row);
        }
        if (r.title && r.price > 0) {
          const key = `${r.kind}|${norm(r.title)}|${norm(r.unit)}`;
          if (!priceGroups.has(key)) priceGroups.set(key, []);
          priceGroups.get(key).push(r);
        }
      });
      if (rows.length && sheetTotal === 0) {
        issues.push({ sev: "warn", sheet: name, cell: "B9", msg: "ფურცელზე ჯამი 0-ია — შევსებული არ არის" });
      }
    });

    // Same resource priced differently on different lines/sheets.
    priceGroups.forEach((list) => {
      const prices = list.map((x) => x.price);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (list.length > 1 && max / min > 1.005) {
        const first = list[0];
        const sample = list
          .slice(0, 4)
          .map((x) => `${x.price.toFixed(2)} (${x.sheet.split("_")[0]})`)
          .join(" · ");
        issues.push({
          sev: "warn", sheet: first.sheet, cell: `F${first.row}`,
          msg: `${first.title}: სხვადასხვა ფასი — ${sample}`,
        });
      }
    });

    const order = { error: 0, warn: 1, info: 2 };
    issues.sort((a, b) => order[a.sev] - order[b.sev]);
    return {
      issues,
      sheetCount: sheets.length,
      rowCount,
      errors: issues.filter((i) => i.sev === "error").length,
      warnings: issues.filter((i) => i.sev === "warn").length,
    };
  });
}

// ───────────────────────── Procurement lists ─────────────────────────

export async function buildProcurementSheet({ projectName = "" } = {}) {
  return Excel.run(async (context) => {
    const sheets = await readChildSheets(context);
    const groups = new Map();
    sheets.forEach(({ name, rows }) => {
      rows.forEach((r) => {
        if (!r.kind || !r.title || !r.qty) return;
        const key = `${r.kind}|${norm(r.title)}|${norm(r.unit)}`;
        const g = groups.get(key) || { kind: r.kind, title: r.title, unit: r.unit, qty: 0, cost: 0, min: Infinity, max: 0, sheets: new Set() };
        g.qty += r.qty;
        g.cost += r.qty * r.price;
        g.min = Math.min(g.min, r.price);
        g.max = Math.max(g.max, r.price);
        g.sheets.add(name.split("_")[0]);
        groups.set(key, g);
      });
    });
    if (!groups.size) throw new Error("ფასიანი რესურსები ვერ მოიძებნა დეტალურ ფურცლებზე.");

    const SECTIONS = [
      ["MATERIAL", "მასალები — შესაძენი რაოდენობები"],
      ["LABOUR", "შრომა — საჭირო ადამიანსაათები"],
      ["EQUIPMENT", "ტექნიკა — საჭირო მანქანასაათები"],
    ];

    const name = "მასალების უწყისი";
    let sheet = context.workbook.worksheets.getItemOrNullObject(name);
    await context.sync();
    if (sheet.isNullObject) sheet = context.workbook.worksheets.add(name);
    else sheet.getUsedRange().clear(Excel.ClearApplyTo.all);

    [[18], [36], [320], [70], [95], [85], [85], [95], [110], [70], [170]].forEach(([w], i) => {
      sheet.getRangeByIndexes(0, i, 1, 1).format.columnWidth = w;
    });
    const CUR = '#,##0.00 "₾"';
    const QTY = "#,##0.###";

    sheet.getRange("B2").values = [[`მასალების, შრომისა და ტექნიკის უწყისი${projectName ? ` — ${projectName}` : ""}`]];
    sheet.getRange("B2:K2").merge();
    const banner = sheet.getRange("B2:K2");
    banner.format.fill.color = "#00B3B3";
    banner.format.font.color = "white";
    banner.format.font.bold = true;
    banner.format.font.size = 12;
    sheet.getRange("2:2").format.rowHeight = 26;

    let r = 4;
    let grand = 0;
    const totalRows = [];
    SECTIONS.forEach(([kind, label]) => {
      const list = [...groups.values()].filter((g) => g.kind === kind).sort((a, b) => b.cost - a.cost);
      if (!list.length) return;

      sheet.getRange(`B${r}`).values = [[label]];
      sheet.getRange(`B${r}:K${r}`).merge();
      const sec = sheet.getRange(`B${r}:K${r}`);
      sec.format.fill.color = "#1B2A33";
      sec.format.font.color = "white";
      sec.format.font.bold = true;
      r += 1;

      sheet.getRange(`B${r}:K${r}`).values = [["N", "დასახელება", "ერთ.", "რაოდენობა", "საშ. ფასი", "მინ. ფასი", "მაქს. ფასი", "ჯამი", "წილი", "ფურცელი"]];
      const head = sheet.getRange(`B${r}:K${r}`);
      head.format.fill.color = "#DEEAF6";
      head.format.font.bold = true;
      head.format.horizontalAlignment = "Center";
      r += 1;

      const first = r;
      const sectionCost = list.reduce((s, g) => s + g.cost, 0);
      let running = 0;
      const rows = list.map((g, i) => {
        running += g.cost;
        const abc = running / sectionCost <= 0.8 ? "A" : running / sectionCost <= 0.95 ? "B" : "C";
        return [i + 1, g.title, g.unit, g.qty, g.qty ? g.cost / g.qty : 0, g.min, g.max, "", "", `${abc} · ${[...g.sheets].slice(0, 6).join(", ")}`];
      });
      sheet.getRangeByIndexes(first - 1, 1, rows.length, 10).values = rows;
      const last = first + rows.length - 1;
      for (let i = 0; i < rows.length; i += 1) {
        const rr = first + i;
        sheet.getRange(`I${rr}`).formulas = [[`=E${rr}*F${rr}`]];
        sheet.getRange(`J${rr}`).formulas = [[`=IF(I${last + 1}=0,0,I${rr}/I${last + 1})`]];
      }
      sheet.getRange(`E${first}:E${last}`).numberFormat = rows.map(() => [QTY]);
      sheet.getRange(`F${first}:I${last}`).numberFormat = rows.map(() => [CUR, CUR, CUR, CUR]);
      sheet.getRange(`J${first}:J${last}`).numberFormat = rows.map(() => ["0.0%"]);

      sheet.getRange(`C${last + 1}`).values = [["ჯამი"]];
      sheet.getRange(`I${last + 1}`).formulas = [[`=SUM(I${first}:I${last})`]];
      sheet.getRange(`I${last + 1}`).numberFormat = [[CUR]];
      const tot = sheet.getRange(`B${last + 1}:K${last + 1}`);
      tot.format.fill.color = "#00B3B3";
      tot.format.font.color = "white";
      tot.format.font.bold = true;
      totalRows.push(last + 1);
      grand += sectionCost;
      r = last + 3;
    });

    sheet.getRange(`C${r}`).values = [["პირდაპირი ხარჯი სულ"]];
    sheet.getRange(`I${r}`).formulas = [[`=${totalRows.map((t) => `I${t}`).join("+")}`]];
    sheet.getRange(`I${r}`).numberFormat = [[CUR]];
    const g = sheet.getRange(`B${r}:K${r}`);
    g.format.fill.color = "#1B2A33";
    g.format.font.color = "white";
    g.format.font.bold = true;

    sheet.activate();
    await context.sync();
    return { items: groups.size, total: grand };
  });
}

// ───────────────────────── Scale quantities ─────────────────────────

/** Names of all detail sheets (for the picker). */
export async function listChildSheetNames() {
  return Excel.run(async (context) => (await readChildSheets(context)).map((s) => s.name));
}

/**
 * Multiplies the quantity column (E) of the given detail sheets by `factor`.
 * Cells that hold formulas are left alone. With dryRun, only counts.
 */
export async function scaleQuantities({ factor, sheetNames, dryRun = false }) {
  if (!(factor > 0)) throw new Error("კოეფიციენტი 0-ზე მეტი უნდა იყოს.");
  return Excel.run(async (context) => {
    const sheets = (await readChildSheets(context)).filter((s) => sheetNames.includes(s.name));
    let cells = 0;
    let skippedFormulas = 0;
    sheets.forEach(({ name, rows }) => {
      const ws = context.workbook.worksheets.getItem(name);
      rows.forEach((r) => {
        if (!r.hasQty || !r.qty) return;
        if (r.qtyIsFormula) { skippedFormulas += 1; return; }
        cells += 1;
        if (!dryRun) {
          ws.getRange(`E${r.row}`).values = [[Math.round(r.qty * factor * 1e6) / 1e6]];
        }
      });
    });
    await context.sync();
    return { sheets: sheets.length, cells, skippedFormulas };
  });
}


// ───────────────────── Volume statement (მოცულობათა უწყისი) ─────────────────────
// A sheet that lists every work header row of every detail sheet (the row holding the
// work volume in column E). The user types the new volumes and applies them; the resource
// rows below recalculate from that cell.

const VOLUME_SHEET = "მოცულობათა უწყისი";

export async function createVolumeSheet() {
  return Excel.run(async (context) => {
    const sheets = await readChildSheets(context);
    const rows = [];
    sheets.forEach(({ name, rows: list }) => {
      list.forEach((r) => {
        if (r.id && r.title && !r.hasAnyPrice) {
          rows.push([name, r.row, r.id, r.title, r.unit, r.hasQty ? r.qty : "", ""]);
        }
      });
    });
    if (!rows.length) throw new Error("სამუშაოს სტრიქონები ვერ მოიძებნა დეტალურ ფურცლებზე.");

    let sheet = context.workbook.worksheets.getItemOrNullObject(VOLUME_SHEET);
    await context.sync();
    if (sheet.isNullObject) sheet = context.workbook.worksheets.add(VOLUME_SHEET);
    else sheet.getUsedRange().clear(Excel.ClearApplyTo.all);

    [200, 50, 60, 320, 70, 110, 130].forEach((w, i) => {
      sheet.getRangeByIndexes(0, i, 1, 1).format.columnWidth = w;
    });
    sheet.getRange("A1").values = [["მოცულობათა უწყისი — ჩაწერე ახალი მოცულობა ბოლო სვეტში და დააჭირე „მოცულობების გამოყენებას“"]];
    sheet.getRange("A1:G1").merge();
    sheet.getRange("A1").format.font.bold = true;

    sheet.getRange("A3:G3").values = [["ფურცელი", "სტრ.", "N", "სამუშაო", "ერთ.", "ახლანდელი", "ახალი მოცულობა"]];
    const head = sheet.getRange("A3:G3");
    head.format.fill.color = "#1B2A33";
    head.format.font.color = "white";
    head.format.font.bold = true;

    sheet.getRangeByIndexes(3, 0, rows.length, 7).values = rows;
    sheet.getRangeByIndexes(3, 0, rows.length, 3).format.font.color = "#8a97ad";
    const input = sheet.getRangeByIndexes(3, 6, rows.length, 1);
    input.format.fill.color = "#FEF3C7";
    sheet.activate();
    await context.sync();
    return { rows: rows.length };
  });
}

export async function applyVolumeSheet() {
  return Excel.run(async (context) => {
    const vs = context.workbook.worksheets.getItemOrNullObject(VOLUME_SHEET);
    await context.sync();
    if (vs.isNullObject) throw new Error("ჯერ შექმენი მოცულობათა უწყისი.");
    const used = vs.getUsedRange();
    used.load("values");
    await context.sync();

    const sheets = await readChildSheets(context);
    const current = new Map();
    sheets.forEach(({ name, rows }) => rows.forEach((r) => current.set(`${name}|${r.row}`, r)));

    let updated = 0;
    let unchanged = 0;
    const skipped = [];
    used.values.slice(3).forEach((v) => {
      const [sheetName, row, , title, , , next] = v;
      if (next === "" || next === null || !sheetName) return;
      const value = Number(String(next).replace(",", "."));
      if (!Number.isFinite(value) || value < 0) {
        skipped.push(`${sheetName} #${row}: არასწორი რიცხვი`);
        return;
      }
      const cur = current.get(`${sheetName}|${row}`);
      if (!cur || cur.title !== String(title).trim()) {
        skipped.push(`${sheetName} #${row}: სტრიქონი შეიცვალა — შექმენი უწყისი თავიდან`);
        return;
      }
      if (cur.qtyIsFormula) {
        skipped.push(`${sheetName} #${row}: მოცულობა ფორმულაა`);
        return;
      }
      if (Math.abs(cur.qty - value) < 1e-9) {
        unchanged += 1;
        return;
      }
      context.workbook.worksheets.getItem(sheetName).getRange(`E${row}`).values = [[value]];
      updated += 1;
    });
    await context.sync();
    return { updated, unchanged, skipped };
  });
}

// ───────────────────── Supplier quote requests (RFQ) ─────────────────────

export async function buildRfqSheet(rfq, projectName = "") {
  return Excel.run(async (context) => {
    const suppliers = rfq.suppliers || [];
    if (!suppliers.length && !(rfq.unassigned || []).length) {
      throw new Error("მოთხოვნისთვის რესურსები ვერ მოიძებნა (საჭიროა კატალოგის კოდით დამატებული მასალები).");
    }
    const name = "მოთხოვნა მომწოდებლებს";
    let sheet = context.workbook.worksheets.getItemOrNullObject(name);
    await context.sync();
    if (sheet.isNullObject) sheet = context.workbook.worksheets.add(name);
    else sheet.getUsedRange().clear(Excel.ClearApplyTo.all);

    [18, 36, 100, 300, 60, 90, 90, 110, 110].forEach((w, i) => { sheet.getRangeByIndexes(0, i, 1, 1).format.columnWidth = w; });
    const CUR = '#,##0.00 "₾"';
    sheet.getRange("B2").values = [[`ფასის მოთხოვნა მომწოდებლებისგან${projectName ? ` — ${projectName}` : ""}`]];
    sheet.getRange("B2:I2").merge();
    const banner = sheet.getRange("B2:I2");
    banner.format.fill.color = "#00B3B3";
    banner.format.font.color = "white";
    banner.format.font.bold = true;
    banner.format.font.size = 12;

    let r = 4;
    suppliers.forEach((sp) => {
      const contact = [sp.contact_person, sp.contact_email, sp.contact_phone].filter(Boolean).join(" · ");
      sheet.getRange(`B${r}`).values = [[`${sp.name}${contact ? "  |  " + contact : ""}${sp.payment_terms ? "  |  " + sp.payment_terms : ""}`]];
      sheet.getRange(`B${r}:I${r}`).merge();
      const hd = sheet.getRange(`B${r}:I${r}`);
      hd.format.fill.color = "#1B2A33";
      hd.format.font.color = "white";
      hd.format.font.bold = true;
      r += 1;
      sheet.getRange(`B${r}:I${r}`).values = [["N", "კოდი", "დასახელება", "ერთ.", "რაოდენობა", "ბოლო ფასი", "თქვენი ფასი", "სავარაუდო ჯამი"]];
      const cols = sheet.getRange(`B${r}:I${r}`);
      cols.format.fill.color = "#DEEAF6";
      cols.format.font.bold = true;
      r += 1;
      const first = r;
      sp.items.forEach((it, i) => {
        sheet.getRange(`B${r}:G${r}`).values = [[i + 1, it.code, it.name, it.unit || "", it.qty, it.offer_rate]];
        sheet.getRange(`I${r}`).formulas = [[`=F${r}*G${r}`]];
        r += 1;
      });
      const last = r - 1;
      sheet.getRange(`H${first}:H${last}`).format.fill.color = "#FEF3C7";
      sheet.getRange(`F${first}:F${last}`).numberFormat = sp.items.map(() => ["#,##0.###"]);
      sheet.getRange(`G${first}:G${last}`).numberFormat = sp.items.map(() => [CUR]);
      sheet.getRange(`I${first}:I${last}`).numberFormat = sp.items.map(() => [CUR]);
      sheet.getRange(`E${r}`).values = [["ჯამი"]];
      sheet.getRange(`I${r}`).formulas = [[`=SUM(I${first}:I${last})`]];
      sheet.getRange(`I${r}`).numberFormat = [[CUR]];
      sheet.getRange(`B${r}:I${r}`).format.font.bold = true;
      r += 2;
    });

    if ((rfq.unassigned || []).length) {
      sheet.getRange(`B${r}`).values = [["მომწოდებელი არ არის მიბმული (კოდი ან მიმდინარე ფასი არ აქვს)"]];
      sheet.getRange(`B${r}:I${r}`).merge();
      const hd = sheet.getRange(`B${r}:I${r}`);
      hd.format.fill.color = "#7a4a00";
      hd.format.font.color = "white";
      hd.format.font.bold = true;
      r += 1;
      const rows = rfq.unassigned.map((it, i) => [i + 1, "", it.name, it.unit || "", it.qty, it.current_rate]);
      sheet.getRangeByIndexes(r - 1, 1, rows.length, 6).values = rows;
      sheet.getRange(`F${r}:F${r + rows.length - 1}`).numberFormat = rows.map(() => ["#,##0.###"]);
    }
    sheet.activate();
    await context.sync();
    return { suppliers: suppliers.length, unassigned: (rfq.unassigned || []).length };
  });
}

// ───────────────────── Payment schedule (cash flow) ─────────────────────

export async function buildScheduleSheet({ projectName = "", startMonth, advancePct = 0, chapters }) {
  return Excel.run(async (context) => {
    const plan = distributeSchedule(chapters, advancePct);
    if (!plan.months) throw new Error("გრაფიკისთვის თავები ვერ მოიძებნა.");
    const colName = (i) => {
      let n = i + 1;
      let out = "";
      while (n > 0) { const m = (n - 1) % 26; out = String.fromCharCode(65 + m) + out; n = Math.floor((n - 1) / 26); }
      return out;
    };
    const FIRST = 4; // month columns start at E (index 4)
    const lastCol = colName(FIRST + plan.months - 1);
    const CUR = '#,##0.00 "₾"';

    const name = "გრაფიკი";
    let sheet = context.workbook.worksheets.getItemOrNullObject(name);
    await context.sync();
    if (sheet.isNullObject) sheet = context.workbook.worksheets.add(name);
    else {
      sheet.charts.load("items");
      await context.sync();
      sheet.charts.items.forEach((c) => c.delete());
      sheet.getUsedRange().clear(Excel.ClearApplyTo.all);
    }

    sheet.getRange("A:A").format.columnWidth = 18;
    sheet.getRange("B:B").format.columnWidth = 40;
    sheet.getRange("C:C").format.columnWidth = 240;
    sheet.getRange("D:D").format.columnWidth = 110;
    sheet.getRangeByIndexes(0, FIRST, 1, plan.months).format.columnWidth = 90;

    sheet.getRange("B2").values = [[`გადახდების გრაფიკი${projectName ? ` — ${projectName}` : ""}`]];
    sheet.getRange(`B2:${lastCol}2`).merge();
    const banner = sheet.getRange(`B2:${lastCol}2`);
    banner.format.fill.color = "#00B3B3";
    banner.format.font.color = "white";
    banner.format.font.bold = true;
    banner.format.font.size = 12;

    // advance % input
    sheet.getRange("C3").values = [["ავანსი (% პირველ თვეში)"]];
    sheet.getRange("D3").values = [[(Number(advancePct) || 0) / 100]];
    sheet.getRange("D3").numberFormat = [["0%"]];
    sheet.getRange("D3").format.fill.color = "#FEF3C7";

    const labels = Array.from({ length: plan.months }, (_, i) => monthLabel(startMonth, i));
    const head = ["N", "თავი", "ჯამი", ...labels];
    sheet.getRangeByIndexes(4, 1, 1, head.length).values = [head];
    const hr = sheet.getRangeByIndexes(4, 1, 1, head.length);
    hr.format.fill.color = "#1B2A33";
    hr.format.font.color = "white";
    hr.format.font.bold = true;
    hr.format.horizontalAlignment = "Center";

    const first = 6; // first chapter row (1-based)
    const rows = chapters.map((c, i) => [c.number, c.name, Number(c.total) || 0, ...plan.perChapter[i]]);
    sheet.getRangeByIndexes(first - 1, 1, rows.length, head.length).values = rows;
    const last = first + rows.length - 1;
    sheet.getRange(`D${first}:${lastCol}${last}`).numberFormat = rows.map(() => Array(head.length - 2).fill(CUR));

    const w = last + 1;   // work done per month
    const p = last + 2;   // payments
    const cu = last + 3;  // cumulative
    const pc = last + 4;  // cumulative %
    sheet.getRange(`C${w}`).values = [["შესრულებული სამუშაო"]];
    sheet.getRange(`C${p}`).values = [["გადასახდელი (ავანსის გათვალისწინებით)"]];
    sheet.getRange(`C${cu}`).values = [["კუმულაციური გადახდა"]];
    sheet.getRange(`C${pc}`).values = [["% ჯამიდან"]];
    sheet.getRange(`D${w}`).formulas = [[`=SUM(D${first}:D${last})`]];
    for (let i = 0; i < plan.months; i += 1) {
      const col = colName(FIRST + i);
      sheet.getRange(`${col}${w}`).formulas = [[`=SUM(${col}${first}:${col}${last})`]];
      sheet.getRange(`${col}${p}`).formulas = [[
        i === 0
          ? `=${col}${w}*(1-$D$3)+$D$3*$D$${w}`
          : `=${col}${w}*(1-$D$3)`,
      ]];
      sheet.getRange(`${col}${cu}`).formulas = [[`=SUM($${colName(FIRST)}${p}:${col}${p})`]];
      sheet.getRange(`${col}${pc}`).formulas = [[`=IF($D$${w}=0,0,${col}${cu}/$D$${w})`]];
    }
    sheet.getRange(`D${w}:${lastCol}${cu}`).numberFormat = [0, 1, 2].map(() => Array(head.length - 2).fill(CUR));
    sheet.getRange(`E${pc}:${lastCol}${pc}`).numberFormat = [Array(plan.months).fill("0.0%")];
    const totals = sheet.getRange(`B${w}:${lastCol}${pc}`);
    totals.format.font.bold = true;
    sheet.getRange(`B${w}:${lastCol}${w}`).format.fill.color = "#DEEAF6";
    sheet.getRange(`B${p}:${lastCol}${p}`).format.fill.color = "#00B3B3";
    sheet.getRange(`B${p}:${lastCol}${p}`).format.font.color = "white";

    sheet.activate();
    await context.sync();

    // S-curve chart (best effort — not every host supports charts)
    try {
      const chart = sheet.charts.add(Excel.ChartType.line, sheet.getRange(`E${cu}:${lastCol}${cu}`), Excel.ChartSeriesBy.rows);
      chart.title.text = "კუმულაციური გადახდები";
      chart.legend.visible = false;
      const series = chart.series.getItemAt(0);
      series.name = "კუმულაციური გადახდა";
      series.setXAxisValues(sheet.getRange(`E5:${lastCol}5`));
      chart.setPosition(`B${pc + 2}`, `${lastCol}${pc + 18}`);
      await context.sync();
    } catch (e) {
      console.warn("Schedule chart skipped:", e);
    }
    return { months: plan.months, grand: plan.grand };
  });
}

export { colLetter };
